import { Field } from '../field/models';
import { Characteristic, Phenomenon, Region, User, UUID } from '../models';
import { EditMeasurementByInspectionRequestParams, EditMeasuresRequestParams, Measurement, Monitoring } from './models';
import { getValueLanguage } from '../language/utils';
import { buildMeasurementKey, getAllParentsAsString } from './utils';

type TranslatedName = {
  translatedName?: ReturnType<typeof getValueLanguage>;
};

export const buildMeasurements = (
  monitorings: Monitoring[],
  phenomena: Phenomenon[],
  users: User[],
  fields: Field[],
  propertyId: UUID,
  regions: Region[],
  measurementByInspectionEnabled = false
): Measurement[] => {
  const phenomenonByCharacteristicId: Record<string, Phenomenon & TranslatedName> = {};
  const phenomenonCharacteristicsById: Record<string, Characteristic & TranslatedName> = {};

  const regionNames = regions.map(region => region.name);

  phenomena.forEach(phenomenon => {
    phenomenon.characteristics?.forEach(characteristic => {
      const { id } = characteristic;

      if (id) {
        if (!phenomenonByCharacteristicId[id]) {
          phenomenonByCharacteristicId[id] = phenomenon;
          phenomenonByCharacteristicId[id].translatedName = getValueLanguage(phenomenon.name);
        }

        if (!phenomenonCharacteristicsById[id]) {
          phenomenonCharacteristicsById[id] = characteristic;
          phenomenonCharacteristicsById[id].translatedName = getValueLanguage(characteristic.name);
        }
      }
    });
  });

  const usersById: Record<string, User> = {};

  users.forEach(user => {
    const { id } = user;
    if (id && !usersById[id]) {
      usersById[id] = user;
    }
  });

  const fieldsById: Record<string, Field> = {};

  fields.forEach(field => {
    const { id } = field;
    if (id && !fieldsById[id]) {
      fieldsById[id] = field;
    }
  });

  const regionById: Record<string, Region> = {};

  regions.forEach(region => {
    const { id } = region;

    if (id && !regionById[id]) {
      regionById[id] = region;
    }
  });

  const measurements: Measurement[] = [];

  monitorings.forEach(monitoring => {
    const user = usersById[monitoring.scouter_id];
    const field = fieldsById[monitoring.area_id];

    const nodeIdToFindParents = field ? field.parent_region_id : '';

    const node = regionById[nodeIdToFindParents];

    const regionList = node ? getAllParentsAsString(regions, node) : regionNames;

    monitoring.measurements.forEach(measure => {
      const phenomenon = phenomenonByCharacteristicId[measure.characteristic_id];
      const characteristic = phenomenon && phenomenonCharacteristicsById[measure.characteristic_id];

      measurements.push({
        id: measurementByInspectionEnabled
          ? buildMeasurementKey(monitoring.id, measure.inspection_id, measure.characteristic_id)
          : measure.id,
        monitoringId: monitoring.id,
        inspectionId: measure.inspection_id,
        inspectionGroupId: measure.inspection_group_id,
        representedByMonitoringId: monitoring.represented_by,
        propertyId,
        user: user && { id: user.id, name: user.name },
        phenomenon: phenomenon && {
          id: phenomenon.id,
          name: phenomenon.translatedName
        },
        characteristic: characteristic
          ? {
              id: measure.characteristic_id,
              name: characteristic.translatedName,
              inputDefinition: characteristic.input_definition,
              companyId: characteristic.company_id || null,
              parentId: characteristic.parent_id || null,
              phenomenonId: characteristic.phenomenon_id,
              defaultIndicatorId: characteristic.default_indicator_id,
              cropIds: characteristic.crop_ids
            }
          : {
              id: measure.characteristic_id,
              name: measure.characteristic_id
            },
        date: monitoring.start_date,
        area: field && { id: field.id, name: field.name },
        value: measure.value,
        coordinates: monitoring.coordinates,
        region: regionList
      });
    });
  });

  return measurements;
};

export const getEditMeasurementRequest = (measurement: Measurement) => {
  const formattedDate = measurement.date.substring(0, 10);
  return {
    monitoringId: measurement.monitoringId,
    measurementId: measurement.id,
    propertyId: measurement.propertyId,
    startDate: formattedDate,
    value: measurement.value
  };
};

/**
 * A 422 from the measures endpoint carries validation detail per measure instead of in `message`,
 * so the generic error helper would surface only the fallback text.
 */
export const getMeasuresErrorMessage = (error: { response?: { data?: { measures?: Array<{ errors?: string[] }> } } }): string | null => {
  const measures = error?.response?.data?.measures;
  if (!Array.isArray(measures)) return null;

  const messages = measures.reduce<string[]>((acc, measure) => acc.concat(measure?.errors ?? []), []);
  return messages.length > 0 ? messages.join('. ') : null;
};

export const getEditMeasuresRequest = (measurement: Measurement): EditMeasuresRequestParams => {
  return {
    observationId: measurement.monitoringId,
    measures: [
      {
        inspection_group_id: measurement.inspectionGroupId,
        characteristic_id: measurement.characteristic.id,
        value: measurement.value
      }
    ]
  };
};

export const getEditMeasurementByInspectionRequest = (measurement: Measurement): EditMeasurementByInspectionRequestParams => {
  return {
    propertyId: measurement.propertyId,
    observationId: measurement.monitoringId,
    inspectionId: measurement.inspectionId,
    characteristicId: measurement.characteristic.id,
    startDate: measurement.date.substring(0, 10),
    value: measurement.value
  };
};
